/**
 * R58-W-12 — NotificationsDrawer.
 *
 * Lists the caller's notifications. Each row: kind-aware copy +
 * relative-time + Open Dataset deep link. Mark-all-read button at the
 * top. Clicking a row marks-read + navigates.
 *
 * DI-3-S68 C4 (notifications/v1 1.1.0, DI-D70 c4 — "bell is ok, I hate mail
 * notifications") — `intake.stalled`: "<turbine> · <park> — stalled …"; its
 * click opens the set's details dialog on My Intake (`?open=<daId>`). A set
 * that lives in another tenant (a shared workflow's owner) is opened as My
 * Intake opens any shared set: its tenant goes into the set-tenant memory
 * (`setTenants.ts`, the payload's `tenantCode` — the server's word, never
 * typed by anyone), so the intake helpers send it as `X-Tenant-ID`. No
 * tenant switch.
 */
import { useEffect, useMemo, useRef, type ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import {
  useMarkAllRead,
  useMarkRead,
  useNotifications,
  useUnreadCount,
  type NotificationItem,
} from "./queries.js";
import { XIcon } from "@aeriosiq/ui/icons";
import { useAuthStore } from "@aeriosiq/auth-client";
import { rememberSetTenant, setTenantOf } from "../intake/api/setTenants.js";

/** DI-3-S68 — where a notification row leads (null = nowhere). */
export function notificationHref(
  n: Pick<NotificationItem, "kind" | "payload">,
): string | null {
  const daId =
    typeof n.payload.dataAcquisitionId === "string"
      ? n.payload.dataAcquisitionId
      : null;
  if (daId === null) return null;
  if (n.kind === "intake.stalled")
    return `/field-data/intake?open=${encodeURIComponent(daId)}`;
  return `/analysis/${daId}`;
}

/**
 * DI-3-S68 — an `intake.stalled` set living in another tenant: remember its
 * tenant (as a list run for a shared owner does) before My Intake reads it.
 */
export function rememberStalledSetTenant(
  n: Pick<NotificationItem, "kind" | "payload">,
  ownSlug: string | null,
): void {
  if (n.kind !== "intake.stalled") return;
  const daId = n.payload.dataAcquisitionId;
  const code = n.payload.tenantCode;
  if (typeof daId !== "string" || typeof code !== "string" || code === "")
    return;
  if (code === ownSlug || setTenantOf(daId)) return;
  rememberSetTenant(daId, { slug: code, name: null });
}

interface DrawerProps {
  open: boolean;
  onClose: () => void;
}

export function NotificationsDrawer({
  open,
  onClose,
}: DrawerProps): ReactElement {
  const navigate = useNavigate();
  const ownSlug = useAuthStore((s) => s.tenant?.tenantSlug ?? null);
  const listQuery = useNotifications({ pageSize: 50 });
  // DI-3-S68 F9 — the list was read once at mount: a row written later
  // without a live push (the stalled detector) left the drawer at "No
  // notifications yet" beside a badge of 1. Read it again when the drawer
  // opens and whenever the polled unread count changes.
  const unread = useUnreadCount().data?.unreadCount;
  const { refetch } = listQuery;
  const lastUnread = useRef(unread);
  useEffect(() => {
    if (open) void refetch();
  }, [open, refetch]);
  useEffect(() => {
    if (lastUnread.current === unread) return;
    lastUnread.current = unread;
    void refetch();
  }, [unread, refetch]);
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();

  const items = useMemo(
    () => listQuery.data?.items ?? [],
    [listQuery.data?.items],
  );

  const hasUnread = useMemo(
    () => items.some((it) => it.readAt === null),
    [items],
  );

  const handleRowClick = (n: NotificationItem) => {
    if (n.readAt === null) {
      markRead.mutate(n.id);
    }
    const href = notificationHref(n);
    if (href !== null) {
      rememberStalledSetTenant(n, ownSlug);
      void navigate(href);
      onClose();
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      data-testid="notifications-drawer"
      PaperProps={{ sx: { width: { xs: "100%", sm: 420 } } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          gap: 1,
        }}
      >
        <Typography variant="h6" component="h2">
          Notifications
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            size="small"
            disabled={!hasUnread || markAllRead.isPending}
            onClick={() => {
              markAllRead.mutate();
            }}
            data-testid="notifications-mark-all-read"
          >
            Mark all read
          </Button>
          <IconButton
            aria-label="Close notifications"
            onClick={onClose}
            size="small"
          >
            <XIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>
      <Divider />
      {listQuery.isPending ? (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <CircularProgress size={24} />
        </Box>
      ) : items.length === 0 ? (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            No notifications yet.
          </Typography>
        </Box>
      ) : (
        <List dense disablePadding>
          {items.map((n) => (
            <ListItemButton
              key={n.id}
              data-testid={`notification-row-${n.id}`}
              onClick={() => {
                handleRowClick(n);
              }}
              sx={{
                bgcolor:
                  n.readAt === null ? "action.hover" : "background.paper",
              }}
            >
              <ListItemText
                data-kind={n.kind}
                primary={notificationCopy(n)}
                secondary={new Date(n.createdAt).toLocaleString()}
              />
            </ListItemButton>
          ))}
        </List>
      )}
    </Drawer>
  );
}

/** DI-3-S68 — "WEA 07 · Windpark Eggersdorf" from the payload (null parts left out). */
function stalledSubject(payload: NotificationItem["payload"]): string {
  const parts = [payload.turbine, payload.park].filter(
    (x): x is string => typeof x === "string" && x.trim() !== "",
  );
  return parts.length > 0 ? parts.join(" · ") : "A data set in the intake";
}

export function notificationCopy(n: NotificationItem): string {
  const payload = n.payload;
  const daId =
    typeof payload.dataAcquisitionId === "string"
      ? payload.dataAcquisitionId
      : "(unknown)";
  const shortId = daId.length >= 8 ? daId.slice(0, 8) : daId;
  const byName =
    typeof payload.byDisplayName === "string" &&
    payload.byDisplayName.length > 0
      ? payload.byDisplayName
      : "Someone";
  const isSelf = payload.reason === "self_claim";
  switch (n.kind) {
    case "data_acquisition.assigned":
      return isSelf
        ? `You claimed dataset ${shortId}`
        : `${byName} assigned dataset ${shortId} to you`;
    case "data_acquisition.auto_assigned":
      return `You auto-claimed dataset ${shortId} by editing`;
    case "data_acquisition.unassigned":
      return `Dataset ${shortId} was unassigned`;
    case "intake.stalled":
      return payload.stalledReason === "run_dead"
        ? `${stalledSubject(payload)} — processing stopped. Open it to Re-check.`
        : `${stalledSubject(payload)} is stalled — finish it in the browser that started it, or withdraw it.`;
    default:
      // Defensive — future kinds extend the enum.
      return `Notification on dataset ${shortId}`;
  }
}
